import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import backgroundImage from "../assets/background.jpg"; // Placeholder for background image
import { FaWhatsapp } from "react-icons/fa";
import { formatInTimeZone } from "date-fns-tz";

const Blogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch("/api/blogs");
      if (!response.ok) {
        throw new Error("Failed to fetch blogs");
      }
      const data = await response.json();
      setBlogs(data);
      setFilteredBlogs(data);
    } catch (err) {
      setError(err.message || "Error fetching blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();

    const ws = new WebSocket("ws://localhost:8080");
    ws.onopen = () => {
      console.log("WebSocket connected");
    };
    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      if (message.type === "reloadBlogs") {
        fetchBlogs();
      }
    };
    ws.onclose = () => {
      console.log("WebSocket disconnected");
    };
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    return () => {
      ws.close();
    };
  }, []);

  useEffect(() => {
    const filtered = blogs.filter(
      (blog) =>
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBlogs(filtered);
  }, [searchTerm, blogs]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        backgroundRepeat: "no-repeat",
        backgroundColor: "#1e3a8a",
        background: `linear-gradient(135deg, rgba(30, 58, 138, 0.8), rgba(6, 182, 212, 0.8)), url(${backgroundImage})`,
      }}
    >
      {/* Hero Banner */}
      <div className="bg-black/30 backdrop-blur-sm text-white py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 animate-fade-in">
            Virtueista IT Blog
            <span className="block text-yellow-500 mt-2">
              Insights, Tips, and Tech Trends
            </span>
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto mb-6">
            Stay updated with the latest in IT training, software development, and
            career advice from our experts in Biratnagar, Nepal.
          </p>
          <button
            onClick={() => navigate("/send-inquiry")}
            className="bg-yellow-500 text-blue-900 py-3 px-6 rounded-md font-semibold hover:bg-yellow-600 transform hover:scale-105 transition-all duration-300 shadow-md"
          >
            Subscribe for Updates
          </button>
        </div>
      </div>

      {/* Blog Content */}
      <div className="max-w-7xl mx-auto px-4 py-12 md:py-16 bg-gray-50 rounded-xl">
        {/* Search Bar */}
        <div className="mb-12">
          <div className="relative max-w-lg mx-auto">
            <input
              type="text"
              placeholder="Search blogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 border rounded-md text-gray-600 focus:ring-2 focus:ring-blue-500 transition-all"
            />
            <svg
              className="absolute right-3 top-3 w-5 h-5 text-gray-400"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
          </div>
        </div>

        {/* Blog Posts */}
        <div className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-8">
            Latest Posts
          </h2>
          {filteredBlogs.length === 0 ? (
            <p className="text-center text-gray-600">No blogs found.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {filteredBlogs.map((post) => (
                <div
                  key={post.id}
                  className="bg-white/10 backdrop-blur-sm rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300"
                >
                  <div className="p-6">
                    <span className="inline-block bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded mb-2">
                      {post.category}
                    </span>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>
                    <div className="flex justify-between text-gray-500 text-sm">
                      <span>
                        {formatInTimeZone(new Date(post.date), "Asia/Kathmandu", "yyyy-MM-dd")}
                      </span>
                      <span>{post.readTime}</span>
                    </div>
                    <button
                      onClick={() => alert("Blog post coming soon!")}
                      className="mt-4 w-full bg-blue-600 text-white py-2 rounded-md font-semibold hover:bg-blue-700 transition-colors duration-200"
                    >
                      Read More
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="text-center bg-black/30 backdrop-blur-sm text-white py-12 px-4 rounded-xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Never Miss an Update
          </h2>
          <p className="text-lg max-w-2xl mx-auto mb-6">
            Subscribe to our blog for the latest tips, tutorials, and tech trends
            from Virtueista IT.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate("/send-inquiry")}
              className="bg-yellow-500 text-blue-900 py-3 px-6 rounded-md font-semibold hover:bg-yellow-600 transition-all duration-300"
            >
              Subscribe Now
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="bg-white text-blue-800 py-3 px-6 rounded-md font-semibold hover:bg-gray-100 transition-all duration-300"
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/9779849489441"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-4 top-1/2 transform -translate-y-1/2 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 hover:scale-110 transition-all duration-300 z-50"
        aria-label="Chat with us on WhatsApp"
      >
        <FaWhatsapp className="w-6 h-6 animate-spin-slow" />
      </a>

      {/* Inline CSS for Animations */}
      <style>
        {`
          @keyframes fade-in {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fade-in 1s ease-out;
          }
          .hover\\:scale-105:hover {
            transform: scale(1.05);
          }
          @keyframes spin-slow {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .animate-spin-slow {
            animation: spin-slow 4s linear infinite;
          }
          @media (max-width: 640px) {
            div[style*="background-attachment: fixed"] {
              background-attachment: scroll;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Blogs;
